import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { showSuccessToast, showAlertToast } from '@medflex/components/stories/Toast';
import { TRACKING_ACTIONS, trackEvent } from '@medflex/utils/Tracking';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import useChatContacts from './useChatContacts';
import getFullNameWithTitle from '../../utils/getFullNameWithTitle';
import usePermissions from './usePermissions';

const sortByDate =
  ({ key }) =>
  (a, b) =>
    new Date(b[key]) - new Date(a[key]);

const useInvitationRequests = (load = false) => {
  const { invitationRequests } = useSelector(state => state.invitationRequests);
  const dispatch = useDispatch();
  const { contacts, loadChatContacts } = useChatContacts();
  const { t } = useTranslation('physician');
  const history = useHistory();
  const { hasPatientAccess } = usePermissions();

  const deleteInvitationRequest = async ({ id }) => {
    await dispatch.invitationRequests.delete({ id });
  };

  const setIsSeen = () => dispatch.invitationRequests.onSeen();

  const invite = async ({ id }) => {
    try {
      const response = await dispatch.invitationRequests.invite({ id });
      const { data } = response;
      const { id: contactId } = data;
      const contact = contacts.find(c => c.id === contactId);
      if (contact) {
        if (contact.pairedAt) {
          trackEvent({
            zone: 'Invitation_Requests',
            action: TRACKING_ACTIONS.CLICK,
            actionText: 'contact already in contact list (paired)',
          });
          showSuccessToast(
            t('invitation_request.success_existing_paired.title'),
            <span>
              {t('invitation_request.success_existing_paired.description')}
              <button
                type="button"
                className="text-blue-500"
                onClick={() => history.push(`/chat/${contact.id}`)}
              >
                {t('invitation_request.success_existing_paired.button')}
              </button>
            </span>,
            { toastId: 'contactAlreadyPaired' },
          );
        } else {
          trackEvent({
            zone: 'Invitation_Requests',
            action: TRACKING_ACTIONS.CLICK,
            actionText: 'contact already in contact list (unpaired)',
          });
          showSuccessToast(
            t('invitation_request.success_existing_unpaired.title'),
            t('invitation_request.success_existing_unpaired.description'),
            { toastId: 'contactAlreadyInvited' },
          );
        }
      } else {
        setIsSeen();
        loadChatContacts();
        trackEvent({
          zone: 'Invitation_Requests',
          action: TRACKING_ACTIONS.CLICK,
        });
        showSuccessToast(
          t('invitation_request.success_new.title'),
          t('invitation_request.success_new.description'),
          { toastId: 'invitationRequestSent' },
        );
      }
    } catch {
      showAlertToast(
        t('invitation_request.failMessage.title'),
        t('invitation_request.failMessage.description'),
        { toastId: 'invitationRequestFailed' },
      );
    }
  };

  const ignore = ({ id }) => {
    history.push(`?modal=ignoreInvitationRequest&id=${id}`);
  };

  const sortedRequests = useMemo(
    () =>
      invitationRequests.sort(sortByDate({ key: 'createdAt' })).map(req => ({
        ...req,
        createdAt: format(req.createdAt, 'DD.MM.YYYY'),
        dateOfBirth: req.dateOfBirth.split('-').reverse().join('.'),
        name: getFullNameWithTitle.contact(req),
      })),
    [invitationRequests],
  );

  useEffect(() => {
    if (load && hasPatientAccess) dispatch.invitationRequests.load();
  }, []);

  return {
    invitationRequests: sortedRequests,
    invite,
    ignore,
    deleteInvitationRequest,
    setIsSeen,
  };
};

export default useInvitationRequests;
